
/* eslint-disable prefer-destructuring */
import { language, request } from '@tencent/ppd-base-libs';
import { FixedCouponType, ConfigScanGift, ConfigCouponInfo } from '../types';
import { to, cacheFunc, getConfigWithLang } from '@tencent/wphk-base-utils';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';

/** 原始配置-某个时间段的券组合配置 */
type RawConfigCouponInfo = {
  /** 券生效时间 */
  startTime: string;
  /** 券失效时间 */
  endTime: string;
  /** 活动Id列表 */
  actIdList: {
    /** 活动Id加密串 */
    actIdEn: string;
    /** 券类型 */
    type: string,
    /** 可领券时间列表(原始是string，要格式化为二维数组) */
    ableTimeList: string;
  }[];
};
/** 原始配置-商户配置 */
type RawConfigSpId = {
  spId: string;
  /** 开始参与时间 */
  startTime: string;
  /** 结束参与时间 */
  endTime: string;
};
/** 原始配置-组件皮肤 */
export type RawSkin = {
  /** 头部背景图 */
  bgTopImg: string;
  /** 背景色 */
  bgColor: string;
  /** 底部背景图 */
  bgBottomImg: string;
};

/** 原始配置-单个campaignId的配置 */
type RawConfigScanGift = {
  /** 计划Id */
  campaignId: string;
  /** 固定券组合 */
  fixedCouponInfo: RawConfigCouponInfo[];
  /** 可参与商户spId列表 */
  spIdList: RawConfigSpId[];
  /** 规则与协议 */
  tncUrl: string;
  /** 组件皮肤 */
  skin: RawSkin;
  /** 模块自定义标题 */
  title: {
    /** 简体 */
    titleCn: string;
    /** 繁体 */
    titleHk: string;
    /** titleEn */
    titleEn: string;
  }[];
  /** 弹窗组件图片 */
  promptDialogPic: string;
};

/** 格式化后的配置列表 */
let rawScanGiftConf: ConfigScanGift[] = [];
/**
 * 加载配置
 */
export async function loadScanGiftConf(): Promise<ConfigScanGift[]> {
  if (rawScanGiftConf.length > 0) return rawScanGiftConf;
  const rawConf = await promiseGetConfig();
  rawScanGiftConf = formatConfig(rawConf);
  console.log('useScanGift rawScanGiftConf.value', rawScanGiftConf);

  return rawScanGiftConf;
}

/** 加载配置，对错误静默处理 */
const promiseGetConfig = async (): Promise<RawConfigScanGift[]> => {
  const [err, res] = await to(getScanGiftConfig());
  if (err || !res) {
    return [];
  }

  return res;
};
/** 格式化原始配置 */
const formatConfig = (conf: RawConfigScanGift[]): ConfigScanGift[] => {
  // 按照时间，格式化一轮
  const formatConfig: ConfigScanGift[] = (conf as RawConfigScanGift[]).map(item => ({
    campaignId: item.campaignId,
    spIdList: filterAndFormatSpIdList(item.spIdList),
    fixedCouponInfo: filterAndFormatCouponInfo(item.fixedCouponInfo),
    tncUrl: item.tncUrl,
    skin: filterAndFormatSkin(item.skin),
    title: item.title ? getKeyWithLang({ key: 'title', obj: item.title[0] ?? {} }) : '',
    promptDialogPic: item.promptDialogPic?.[0],
  }));
  // 如果没有符合时间的spId 或者 没有符合时间的券，则过滤掉
  const validConfig = formatConfig.filter(item => item.spIdList.length && item.fixedCouponInfo.length);
  /** 多语言处理新规范 */
  return getConfigWithLang(validConfig, language.getLanguage()) as ConfigScanGift[];
};

/** 根据时间过滤spIdList，并格式化后返回(只需要spId列表) */
const filterAndFormatSpIdList = (spIdList: RawConfigSpId[]): string[] => {
  const now = getServerTime();
  // 筛选符合当前时间的，并且只返回spId数组
  return spIdList
    .filter(it => now >= dayjs(it.startTime).toDate() && now < dayjs(it.endTime).toDate())
    .map(it => it.spId);
};
/** 筛选第一条满足当前时间的券组合 */
const filterAndFormatCouponInfo = (couponInfo: RawConfigCouponInfo[]): ConfigCouponInfo[] => {
  const now = getServerTime();
  // 过滤掉不符合时间的
  const filterArr = couponInfo.filter(it => (now >= dayjs(it.startTime).toDate() && now < dayjs(it.endTime).toDate()));

  if (filterArr.length <= 0 || filterArr[0]?.actIdList.length <= 0) return [];

  // 如果有多条满足时间的券组合，只取第1条
  const [firstItem] = filterArr;
  // 格式化
  return firstItem.actIdList.map(it => ({
    actIdEn: it.actIdEn,
    type: (it.type || FixedCouponType.common) as FixedCouponType,
    ableTimeList: handleFixedCouponInfoAbleTime(it.ableTimeList ?? '', firstItem),
  }));
};
/** 筛选并格式化组件皮肤 */
const filterAndFormatSkin = (skin: any): RawSkin => {
  const [firstItem] = skin ?? [];
  const { bgTopImg = '', bgColor = '#FFFFFF', bgBottomImg = '' } = firstItem ?? {};
  return {
    bgTopImg,
    bgColor,
    bgBottomImg,
  };
};
/** 扫码券礼包-处理券可领券时间 */
const handleFixedCouponInfoAbleTime = (ableTimeStr: string, firstItem: RawConfigCouponInfo): string[][] => {
  const { startTime, endTime } = firstItem; // 礼包的开始/结束时间

  // 根据换行符划分ableTimeStr => ['2023/11/01 00:00:00,2023/11/10 23:59:59', '2023/11/10 00:00:00,2023/11/20 23:59:59']
  const groupStrTime = ableTimeStr.replace(/-/g, '/').split('\n');
  // 再以逗号划分开始结束 => [['2023/11/01 00:00:00', '2023/11/10 23:59:59'], ['2023/11/10 00:00:00', '2023/11/20 23:59:59']]
  const groupArrTime = groupStrTime.map(it => it.split(','));
  // 校验时间的有效性，要求：开始时间 < 结束时间 && [开始时间, 结束时间]和[startTime, endTime]有交叉
  const filterAbleTime = groupArrTime.filter((item) => {
    const s = dayjs(item[0]).toDate();
    const e = dayjs(item[1]).toDate();
    return s < e && (s < dayjs(endTime).toDate() || e > dayjs(startTime).toDate());
  });

  return filterAbleTime.length > 0 ? filterAbleTime : [[startTime, endTime]]; // 兜底是全时间有效
};

/** 获取商户配置 - 货源配置系统商户配置 */
const getScanGiftConfig = cacheFunc((): Promise<RawConfigScanGift[]> => request.loadConfig('https://st.moneydata.hk/res/cc/scanGiftCfg/config.json'));

const getKeyWithLang = ({ key, obj }: { key: string, obj: object }) => {
  if (!obj) return '';
  const langMap = {
    zh_cn: 'Cn',
    'zh-cn': 'Cn',
    zh_hk: 'Hk',
    'zh-hk': 'Hk',
    zh_tw: 'Hk',
    'zh-tw': 'Hk',
  };
  const lang = language.getLanguage();
  const postFix = langMap[lang] ?? 'En';

  const targetKey = `${key}${postFix}`;
  return obj[targetKey] || obj[key];
};
